import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Checkbox,
  ListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useSelector } from "react-redux";
import { usePresignedUpload } from "src/hooks/usePresignedUpload";
import { MENU_CATEGORY_IMAGE } from "src/utils/uploadCategories";
import { Edit } from "@mui/icons-material";
import { Delete } from "mdi-material-ui";

export default function CreateCategory({
  editCategory,
  selectedItems,
  setSelectedItems,
  onClose,
  onMenuCreated,
  onAddItemClick,
  isCatalog,
}) {
  const { t } = useTranslation("dashboard");
  const [categoryName, setCategoryName] = useState("");
  const [categoryNameKey, setCategoryNameKey] = useState(""); // original English — used in payload
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const queryClient = useQueryClient();

  // Set default data when editCategory changes — fetch fresh to guarantee nameKey is present
  useEffect(() => {
    if (!editCategory) {
      resetForm();
      return;
    }

    const populate = (cat) => {
      setCategoryName(cat.name || cat.nameKey || "");       // display: translated
      setCategoryNameKey(cat.nameKey || cat.name || "");   // payload: English
      setImagePreview(cat.image || "");
      setSelectedItems(cat.menuItemIds || []);
      setOriginalData(cat);
    };

    const categoryId = editCategory._id || editCategory.id;
    if (categoryId) {
      axiosInstance.get(`${ApiEndPoints.MENU_CATEGORY.get}/${categoryId}`)
        .then((res) => {
          const fresh = res.data?.data;
          populate(fresh && (fresh._id || fresh.id) ? fresh : editCategory);
        })
        .catch(() => populate(editCategory));
    } else {
      populate(editCategory);
    }
  }, [editCategory]);

  const { mutateAsync: uploadFileWithPresignedUrl } = usePresignedUpload();

  // Category creation mutation
  const createCategoryMutation = useMutation(
    async (categoryData) => {
      const response = await axiosInstance.post(
        `${ApiEndPoints.MENU_CATEGORY.add}/${selectedBusinessId}`,
        categoryData
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Refetch categories list in parent component
        queryClient.invalidateQueries(["menuCategory", selectedBusinessId, isCatalog]);
        // Reset form
        resetForm();
        // Call success callback
        onMenuCreated?.(data);
        onClose();
      },
      onError: (error) => {
        console.error("Error creating category:", error);
        setErrors({ submit: t("createCategoryFailedCreate") });
      },
    }
  );

  // Category update mutation
  const updateCategoryMutation = useMutation(
    async (categoryData) => {
      // Handle both _id (MongoDB) and id (transformed) cases
      const categoryId = editCategory._id || editCategory.id;
      const response = await axiosInstance.patch(
        `${ApiEndPoints.MENU_CATEGORY.update}/${categoryId}`,
        categoryData
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Refetch categories list in parent component
        queryClient.invalidateQueries(["menuCategory", selectedBusinessId, isCatalog]);
        // Reset form
        resetForm();
        // Call success callback
        onMenuCreated?.(data);
        onClose();
      },
      onError: (error) => {
        console.error("Error updating category:", error);
        setErrors({ submit: t("createCategoryFailedUpdate") });
      },
    }
  );

  const validateForm = () => {
    const newErrors = {};

    if (!categoryName.trim()) {
      newErrors.categoryName = t("createCategoryNameRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setCategoryName("");
    setImage(null);
    setImagePreview("");
    setCategories([]);
    setSelectedItems([]);
    setErrors({});
    setOriginalData(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImage(null);
      setImagePreview("");
    }
    e.target.value = "";
  };

  const processCategoryPayload = async () => {
    let imageName = null;

    // Handle image upload/retention
    if (image) {
      // New image selected — upload via presigned URL
      const result = await uploadFileWithPresignedUrl({ file: image, category: MENU_CATEGORY_IMAGE });
      imageName = result?.fileUrl;
    } else if (editCategory && editCategory.image) {
      // Keep existing image as-is
      imageName = editCategory.image;
    }

    // Build payload
    const categoryPayload = {
      name: categoryNameKey || categoryName,
      isCatalog: isCatalog, // Add isCatalog to payload
      ...(imageName && { image: imageName }),
    };

         if (selectedItems.length > 0) {
       categoryPayload.menuItemIds = selectedItems.map((item) => item._id || item.id);
     }

    return categoryPayload;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const categoryPayload = await processCategoryPayload();
      console.log("categoryPayload", categoryPayload);

      if (editCategory) {
        // Update existing category
        updateCategoryMutation.mutate(categoryPayload);
      } else {
        // Create new category
        createCategoryMutation.mutate(categoryPayload);
      }
    } catch (error) {
      console.error("Error processing category:", error);
      setErrors({ submit: t("createCategoryFailedProcess") });
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isLoading =
    createCategoryMutation.isLoading ||
    updateCategoryMutation.isLoading;

  return (
    <>
      <Box
        sx={{
          // width: "100vw",
          maxWidth: {
            xs: "100vw",
            sm: "95vw",
            md: 500,
            lg: 600,
            xl: 700,
          },
          mx: "auto",
          bgcolor: "#fff",
          borderRadius: {
            xs: "24px",
            sm: 3,
            md: 4,
          },
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxSizing: "border-box",
          "@media (max-width: 600px)": {
            minWidth: "100vw",
            maxWidth: "100vw",
            borderRadius: "24px",
            boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            width: "100%",
            // background: "linear-gradient(90deg, #6c00e6 0%, #6c00e6 100%)",
            color: "#8000c2",
            px: 0,
            pt: 0,
            pb: 0,
            position: "relative",
            minHeight: 54,
            borderTopLeftRadius: { xs: "24px", sm: 12 },
            borderTopRightRadius: { xs: "24px", sm: 12 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: 8,
              //   background: "linear-gradient(90deg, #6c00e6 0%, #6c00e6 100%)",
              borderTopLeftRadius: { xs: "24px", sm: 12 },
              borderTopRightRadius: { xs: "24px", sm: 12 },
            }}
          />
          <Typography
            sx={{
              pl: 3,
              pt: 1.3,
              fontWeight: 700,
              fontSize: 21,
              color: "#000",
              letterSpacing: 0.2,
              position: "relative",
              zIndex: 1,
              //   background: "#6c00e6",
              width: "fit-content",
              borderTopLeftRadius: 7,
            }}
          >
            {editCategory ? t("createCategoryEdit") : t("createCategoryCreate")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 13,
              color: "#c09adf",
              background: "transparent",
              "&:hover": { color: "#8000c2", background: "#f3e7ff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 27 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: { xs: 2, sm: 4 },
            pt: 2,
            pb: 3,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Error Alert */}
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          {/* Upload section */}
          <Box
            sx={{
              border: "2px dashed #BDBDBD",
              borderRadius: 3,
              minHeight: 95,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              bgcolor: "#faf6ff",
              mb: 0.5,
              justifyContent: "center",
              transition: "border .2s",
              "&:hover": { borderColor: "#8000c2" },
              cursor: "pointer",
              width: "100%",
              boxSizing: "border-box",
            }}
            component="label"
          >
            {imagePreview ? (
              <Avatar
                src={imagePreview}
                alt={t("createCategoryCoverOptional")}
                variant="rounded"
                sx={{
                  width: 90,
                  height: 70,
                  borderRadius: 2,
                  objectFit: "cover",
                  mt: 0.5,
                  mb: 0.5,
                }}
              />
            ) : (
              <Box sx={{ textAlign: "center", py: 1.2 }}>
                <CloudUploadOutlinedIcon
                  sx={{ color: "#ae61e3", fontSize: 30, mb: 0.5 }}
                />
                <Typography
                  sx={{
                    color: "#8000c2",
                    fontSize: 16,
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {t("createCategoryUploadPic")}
                </Typography>
              </Box>
            )}
            <input
              id="category-image-upload"
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageChange}
            />
          </Box>
          <Typography sx={{ color: "#a18fc7", fontSize: 13, mb: 2, ml: 1 }}>
            {t("createCategoryCoverOptional")}
          </Typography>

          {/* Menu name */}
          <TextField
            label={t("createCategoryName")}
            variant="outlined"
            size="small"
            fullWidth
            required
            value={categoryName}
            onChange={(e) => {
              setCategoryName(e.target.value);
              setCategoryNameKey(e.target.value);
            }}
            error={!!errors.categoryName}
            helperText={errors.categoryName}
            sx={{
              mb: 2,
              background: "#f9f7fb",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                fontWeight: 500,
                letterSpacing: 0.1,
              },
            }}
            InputLabelProps={{
              shrink: !!categoryName,
              style: { fontWeight: 400, color: "#bbb" },
            }}
          />

          <Divider sx={{ mb: 2 }} />

          {/* Category section */}
          <Typography
            sx={{
              color: "#a18fc7",
              fontSize: 13,
              mb: 0,
              fontWeight: 400,
              ml: 1,
            }}
          >
            {t("createCategoryItemsLater")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 1,
              mb: 1,
              ml: 1,
              width: "100%",
              boxSizing: "border-box",
            }}
            onClick={onAddItemClick}
          >
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "#fff0e1",
                color: "#E96E25",
                width: 23,
                height: 23,
                mr: 1,
                fontSize: 18,
                borderRadius: "23%",
              }}
            >
              <RestaurantMenuIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Typography
              sx={{
                color: "#c06e24",
                fontSize: 14.5,
                fontWeight: 500,
                mr: 1,
              }}
            >
              {t("createCategoryItemsCount", { count: selectedItems.length })}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "#6c00e6",
                fontSize: 14,
                fontWeight: 500,
                ml: 1,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {t("createCategoryAddItems")}
            </Typography>
          </Box>

           {selectedItems?.map((cat, i) => {
             return (
               <ListItem
                 key={cat._id || cat.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  mb: { xs: 2, sm: 2 },
                  width: "100%",
                  overflow: "hidden",
                  gap: 2,
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#222",
                      fontWeight: 500,
                      fontSize: 15,
                    }}
                  >
                    {i + 1}
                  </Typography>

                                     <Box sx={{ position: "relative", mr: 1 }}>
                     <Avatar
                       variant="rounded"
                       src={cat?.imageVideo?.[0] || cat?.image || undefined}
                       alt={cat.name}
                       sx={{
                         width: 56,
                         height: 56,
                         borderRadius: 2,
                         bgcolor: "#f3f4f6",
                         boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                       }}
                     />
                   </Box>

                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#222",
                      fontSize: 16,
                      minWidth: 120,
                      textTransform: "capitalize",
                    }}
                  >
                    {cat.name}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                  <IconButton
                    size="small"
                    // disabled={isDeleting}
                                         onClick={async (e) => {
                       e.stopPropagation();
                       setSelectedItems(
                         selectedItems.filter((item) => (item._id || item.id) !== (cat._id || cat.id))
                       );
                     }}
                    sx={{
                      color: "#dc2626",
                      "&:hover": {
                        bgcolor: "#fef2f2",
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </ListItem>
            );
          })}
          {/* Save Button */}
          <Button
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{
              mt: 3,
              mb: 1,
              bgcolor: "#6c00e6",
              color: "#fff",
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 17,
              textTransform: "lowercase",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#6c00e6",
              },
              "&:disabled": {
                bgcolor: "#c0c0c0",
              },
              height: 44,
              letterSpacing: 0.3,
            }}
            onClick={handleSave}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : editCategory ? (
              t("createCategoryUpdate")
            ) : (
              t("createCategorySave")
            )}
          </Button>
        </Box>
      </Box>
    </>
  );
}
